/**
 * Phase 21β-3.3 — Schreiben-Composer im Word-Stil (Mietverhältnis · Kontakt · Objekt).
 *
 * Layout:
 *   ┌── Header ──────────────────────────────────────────────────┐
 *   │  Schreiben verfassen · Vorlagen-Bar                         │
 *   ├── Metadaten (Betreff, Unterbetreff, Kategorie, Zeichen) ───┤
 *   ├── WordRibbon (Start · Einfügen · Layout) ──────────────────┤
 *   ├── A4-Papier (TipTap mit tiptap-doc Styling)                │
 *   ├── Versandwege (E-Mail / Post) + Empfängerkontext           │
 *   └── Footer: PDF-Vorschau · Versenden                          │
 *
 * Default-Body enthält nur die Anrede — „Mit freundlichen Grüßen" steht in
 * der User-Signatur (Profil → Stammdaten → Signatur). Backend hängt sie
 * automatisch unter den Brief.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";

import api, { formatApiError } from "@/lib/api";
import { CONTACT_ROLES } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import { buildEditorExtensions } from "@/components/editor/extensions";
import WordRibbon from "@/components/editor/WordRibbon";
import SnippetPicker from "@/components/editor/SnippetPicker";
import CategorySelect from "@/components/editor/CategorySelect";
import RecipientPicker from "@/components/editor/RecipientPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SendMethodModal from "@/components/messaging/SendMethodModal";
import { MobileEditorButton } from "@/components/messaging/MessagingBits";
import WizardRecipientChips from "@/components/messaging/WizardRecipientChips";
import LetterModeToggle from "@/components/messaging/LetterModeToggle";
import AttachmentPicker from "@/components/AttachmentPicker";
import AccountStatementAttach from "@/components/messaging/AccountStatementAttach";
import { openPostPrint } from "@/components/messaging/PostPrintDialog";
import { DatePicker } from "@/components/ui/date-picker";
import { printPdfBlob, fetchPdfBlob } from "@/lib/printPdf";
import {
  Eye, PaperPlaneTilt, Envelope, EnvelopeSimple, FilePdf, FileText, Stack, Download, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const hasSuccessfulChannel = (results) =>
  !!results?.post || ["sent", "queued_no_provider"].includes(results?.email?.status);

export default function MessagingDialog({
  open, onOpenChange, context, defaultSubject = "", defaultEmail = "",
  // VM-7d — Wizard-Modus: Empfänger + Rundschreiben-Optionen
  wizardRecipients = null,
  rundschreiben = false,
  rundschreibenMode = "per_empfaenger",
  // iter145 — Optional: Doc-IDs, die beim Öffnen als Anhang vorausgewählt sein sollen
  // (z. B. wenn ein amtliches Dokument als "Anlage in Editor" übernommen wurde).
  initialAttachmentDocIds = null,
  initialAttachmentDocs = null,
  // 2026-10 · Optional: Vorbelegter Body-Text (z. B. Ratenplan-Erinnerung).
  // Wird beim Öffnen einmal in den Editor gesetzt und dann NICHT weiter erzwungen,
  // damit User-Änderungen erhalten bleiben.
  initialBody = "",
  // Optional: Vorbelegung Unterbetreff / Kategorie / Wiedervorlage (z. B. Mieterhöhung)
  initialSubSubject = "",
  initialCategory = "",
  initialFollowUpDate = "",
  initialMyReference = "",
}) {
  const { user } = useAuth();
  const computedCode = useMemo(() => {
    if (user?.code) return user.code;
    const f = (user?.first_name || "").trim();
    const l = (user?.last_name || "").trim();
    if (f || l) return ((f[0] || "") + (l[0] || "")).toUpperCase();
    return ((user?.name || "")[0] || "").toUpperCase();
  }, [user]);

  const [subject, setSubject] = useState(defaultSubject);
  const [subSubject, setSubSubject] = useState("");
  const subSubjectFromTpl = useRef(false);
  const [channels, setChannels] = useState({ email: false, post: true });
  // Original unterschreiben (Ausdruck + Scan)
  const [wetSignature, setWetSignature] = useState(false);
  const [signedScan, setSignedScan] = useState(null);
  const [scanPending, setScanPending] = useState(false);
  const [wetSigners, setWetSigners] = useState([]);
  const [signerSuggestions, setSignerSuggestions] = useState([]);
  const [emailOverride, setEmailOverride] = useState(defaultEmail);
  const [category, setCategory] = useState("Schriftverkehr");
  const [myReference, setMyReference] = useState("");
  const [theirReference, setTheirReference] = useState("");
  const [templates, setTemplates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [snippetOpen, setSnippetOpen] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  // Phase 21β-4 — Inline-Vorschau (Editor 2) + Font-Voreinstellungen + Letterhead-Picker
  const [livePreview, setLivePreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [userFont, setUserFont] = useState({ family: "", size: "" });
  const [letterheads, setLetterheads] = useState([]);
  const [letterheadId, setLetterheadId] = useState("");
  // 2026-02 — E-Mail-Layouts (Templates)
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplateId, setEmailTemplateId] = useState("");
  // Phase 21β-4.1 — Mehrfach-Empfängerwahl (Mieter / Vertreter)
  const [recipientCandidates, setRecipientCandidates] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  // iter140 — Bei Multi-Mieter mit unterschiedlichen Privatadressen: welcher Kontakt
  // liefert die Adresse im Anschriftfeld? Leerer String = Standard (erster Mieter).
  const [addressFromContactId, setAddressFromContactId] = useState("");
  const [recipientAddresses, setRecipientAddresses] = useState([]);  // [{id,name,addressLine}]
  // iter142 — Meta zum aktuellen MV (zeigt Hinweis, wenn MV noch nicht gestartet)
  const [tenancyMeta, setTenancyMeta] = useState(null); // {contract_start, handover_date, termination_status}
  const [availableEmails, setAvailableEmails] = useState([]);  // P1
  // 2026-02 — Mehrere To-Adressen + BCC-Feld unterstützen.
  // emailToList ist die auswahlbasierte Liste (Chips), emailOverride bleibt für Rückwärtskompatibilität die primäre Adresse.
  const [emailToList, setEmailToList] = useState([]);
  const [emailBcc, setEmailBcc] = useState([]);
  const [emailCc, setEmailCc] = useState([]);
  // Ansprechpartner gewählt → dessen E-Mail ist primärer Empfänger statt der Firmenadresse
  const cpMailApplied = useRef("");
  useEffect(() => {
    const cps = selectedRecipients.map((k) => k.split(":")).filter((p) => p[1] === "cp" && p[2] && p[2] !== "__legacy__");
    const sig = cps.map((p) => p.slice(0, 3).join(":")).join(",");
    if (!open || !cps.length || !availableEmails.length || cpMailApplied.current === sig) return;
    cpMailApplied.current = sig;
    (async () => {
      const add = [], drop = [], prefer = [];
      for (const [cid, , pid] of cps) {
        try {
          const [{ data: person }, { data: company }] = await Promise.all([api.get(`/contacts/${pid}`), api.get(`/contacts/${cid}`)]);
          const norm = (l) => (l || []).map((e) => (typeof e === "string" ? { email: e } : e)).filter((e) => e.email);
          const pm = norm(person.emails);
          const name = `${person.first_name || ""} ${person.last_name || ""}`.trim() || person.company_name || "Ansprechpartner";
          pm.forEach((e) => add.push({ ...e, label: `Ansprechpartner ${name}` }));
          const primary = pm.find((e) => e.is_primary) || pm[0];
          if (primary) { prefer.push(primary.email); norm(company.emails).forEach((e) => drop.push(e.email)); }
        } catch { /* Kontakt nicht ladbar */ }
      }
      setAvailableEmails((cur) => [...add.filter((a) => !cur.some((c) => c.email === a.email)), ...cur]);
      if (prefer.length) setEmailToList((cur) => [...prefer, ...cur.filter((e) => !drop.includes(e) && !prefer.includes(e))]);
    })();
  }, [open, selectedRecipients.join(","), availableEmails.length]);
  // 2026-02 (iter118) — Kenntnisnahme erforderlich
  const [requiresAcknowledgment, setRequiresAcknowledgment] = useState(false);
  const [ackDeadlineDays, setAckDeadlineDays] = useState(14);
  const [contactRole, setContactRole] = useState("");          // P2 Rolle beim Anschreiben
  const [contactRoleOptions, setContactRoleOptions] = useState([]);  // Rollen des Kontakts
  const [filenameContext, setFilenameContext] = useState(""); // P2 freier Kontext
  // Phase VM-3 — Modus-Toggle: Standardschreiben ↔ Vorgangsbezogen
  const [letterMode, setLetterMode] = useState("standard"); // "standard" | "vorgang"
  const [vorgangId, setVorgangId] = useState("");
  const [vorgaenge, setVorgaenge] = useState([]);
  // VM-7b — Mobile-responsive: Sidebar-Drawer
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // VM-12 — Draft-Automatik: id + Save-Status-Anzeige („Entwurf gespeichert um HH:MM")
  const [draftId, setDraftId] = useState(null);
  // iter134 — Speichere die ISO-Zeichenkette 1:1 (nicht als Date), damit die
  // Mikrosekunden-Präzision für den Kollisions-Check erhalten bleibt.
  const [draftSavedIso, setDraftSavedIso] = useState(null);
  const [draftSaving, setDraftSaving] = useState(false);
  // 2026-08 — Exit-Guard: „Wirklich verlassen?" nur wenn nicht-leerer Inhalt
  const [confirmExit, setConfirmExit] = useState(false);
  const justSentRef = useRef(false);
  useEffect(() => { if (open) justSentRef.current = false; else setConfirmExit(false); }, [open]);
  // 2026-08 — Draft-Kollisions-Schutz: Warnung, wenn parallel bearbeitet wurde
  const [conflict, setConflict] = useState(null);
  // 2026-08 (iter111) — Live-Presence: {name, since} eines anderen Bearbeiters
  const [activeEditor, setActiveEditor] = useState(null);
  // Auto-generierter Default-Betreff („Unser Schreiben v. DD.MM.YYYY" oder von defaultSubject-Prop):
  // wird beim Öffnen gesetzt und darf den Exit-Guard NICHT triggern (User hat nichts getippt).
  const [autoDefaultSubject, setAutoDefaultSubject] = useState("");
  // 2026-02 — Anhänge (Document-IDs) für den Mailversand
  const [attachmentDocIds, setAttachmentDocIds] = useState([]);
  const [extraAttachDocs, setExtraAttachDocs] = useState([]);
  const [followUpDate, setFollowUpDate] = useState("");
  // 2026-08 (iter111) — Ticket-Anlagen als E-Mail-Anhang (Bilder aus dem Ticket).
  // {id, filename, content_type, size, ticket_id} — selected = true = mitschicken
  const [ticketAttachments, setTicketAttachments] = useState([]);
  const [ticketAttachmentSelected, setTicketAttachmentSelected] = useState({});

  // ----- Word-style TipTap editor (gleiche Extensions wie Vorlagen-Editor) -----
  const editor = useEditor({
    extensions: buildEditorExtensions({
      placeholder: "Beginne mit dem Schreiben — die Anrede ist bereits eingefügt, die Signatur wird automatisch ergänzt.",
    }),
    content: "<p>{{recipient.greeting}}</p><p></p><p></p>",
    editorProps: {
      attributes: { class: "tiptap-doc" },
      // 2026-02 (feb) — Paste-Verhalten: TipTap-Default nutzen (behält Fettschrift,
      // Absätze etc.). Der frühere Paste-Handler zwang alles auf reinen Text —
      // dadurch gingen Bold/Formatierung verloren und Absätze wurden verdoppelt.
    },
  });

  // Inline-Vorschau-Editor (zeigt gerendertes HTML, bleibt editierbar — der bearbeitete Stand wird beim Versand verwendet)
  const previewEditor = useEditor({
    extensions: buildEditorExtensions({ placeholder: "Vorschau wird geladen…" }),
    content: "",
    editorProps: { attributes: { class: "tiptap-doc" } },
  });

  // ----- Reset state on open + load templates / check signature -----
  useEffect(() => {
    if (!open) return;
    // 2026-07 — Bugfix: State-Leak zwischen zwei aufeinanderfolgenden Schreiben.
    // ALLE empfänger-abhängigen Felder MÜSSEN hier hart zurückgesetzt werden,
    // sonst wandert z. B. die E-Mail des vorherigen Empfängers in das
    // Adressfenster des nächsten Briefs. DSGVO-Risiko!
    setSubject(defaultSubject);
    setSubSubject("");
    subSubjectFromTpl.current = false;
    setWetSignature(false);
    setSignedScan(null);
    setScanPending(false);
    setWetSigners([]);
    setPreview(null);
    setLivePreview(false);
    setMyReference(initialMyReference || computedCode || "");
    setTheirReference("");
    // Explizite Empfänger-Resets:
    setEmailOverride(defaultEmail || "");
    setEmailToList([]);
    setEmailBcc([]);
    setEmailCc([]);
    cpMailApplied.current = "";
    setAvailableEmails([]);
    setContactRole("");
    setAddressFromContactId("");
    setRecipientAddresses([]);
    setTenancyMeta(null);
    setContactRoleOptions([]);
    setAttachmentDocIds(Array.isArray(initialAttachmentDocIds) ? [...initialAttachmentDocIds] : []);
    setExtraAttachDocs(Array.isArray(initialAttachmentDocs) ? [...initialAttachmentDocs] : []);
    setFollowUpDate(initialFollowUpDate || "");
    // 2026-08 (iter111) — Ticket-Anlagen laden, wenn aus einem Ticket geöffnet
    setTicketAttachments([]);
    setTicketAttachmentSelected({});
    if (context?.ticket_id) {
      api.get(`/tickets/${context.ticket_id}`)
        .then(({ data }) => {
          const atts = (data?.attachments || []).map((a) => ({
            ...a, ticket_id: context.ticket_id,
            ticket_number: data.ticket_number,
          }));
          setTicketAttachments(atts);
          // Alle Anhänge standardmäßig vorausgewählt (b). User kann deselektieren.
          const sel = {};
          atts.forEach((a) => { sel[a.id] = true; });
          setTicketAttachmentSelected(sel);
          // Kanal E-Mail vorwählen, wenn Ticket-Anhänge existieren (nur nützlich für E-Mail).
          if (atts.length > 0) setChannels((c) => ({ ...c, email: true }));
        })
        .catch(() => { setTicketAttachments([]); setTicketAttachmentSelected({}); });
    }
    setFilenameContext("");
    setLetterheadId("");
    setCategory(initialCategory || "Schriftverkehr");
    if (initialSubSubject) setSubSubject(initialSubSubject);
    setChannels({ email: false, post: true });
    // 2026-07 — Neuer Default-Betreff: "Unser Schreiben v. DD.MM.YYYY" wenn nichts vom Aufrufer vorgegeben
    if (!defaultSubject) {
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, "0");
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const yyyy = now.getFullYear();
      const autoSubj = `Unser Schreiben v. ${dd}.${mm}.${yyyy}`;
      setSubject(autoSubj);
      setAutoDefaultSubject(autoSubj);
    } else {
      setAutoDefaultSubject(defaultSubject);
    }
    api.get("/templates").then(({ data }) => setTemplates((data || []).filter((x) => !x.system_key)));
    api.get("/letterheads").then(({ data }) => setLetterheads(data || [])).catch(() => setLetterheads([]));
    // 2026-02 — E-Mail-Layouts laden + Default vorwählen
    api.get("/email-templates").then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      setEmailTemplates(list);
      const def = list.find((t) => t.is_default) || list[0];
      setEmailTemplateId(def?.id || "");
    }).catch(() => setEmailTemplates([]));

    // Phase 21β-4.1 — Empfänger-Kandidaten laden (Mietverhältnis ODER Einzelkontakt)
    if (context?.tenancy_id) {
      api.get(`/tenancies/${context.tenancy_id}/recipients`)
        .then(({ data }) => {
          const list = Array.isArray(data) ? data : [];
          setRecipientCandidates(list);
          setSelectedRecipients(list.filter((r) => r.default).map((r) => r.key));
        })
        .catch(() => { setRecipientCandidates([]); setSelectedRecipients([]); });
      // iter142 — MV-Meta für Future-Start-Hinweis laden
      api.get(`/tenancies/${context.tenancy_id}`)
        .then(({ data }) => setTenancyMeta({
          contract_start: data?.contract_start,
          handover_date: data?.handover_date,
          termination_status: data?.termination_status,
        }))
        .catch(() => setTenancyMeta(null));
    } else if (context?.contact_id) {
      // 2026-08 · Einzelkontakt: Firma + verknüpfte Ansprechpartner als Empfänger-Optionen
      // 2026-10 · Respektiert preselected_recipient_keys (z. B. AP direkt vorwählen)
      api.get(`/contacts/${context.contact_id}/recipients`)
        .then(({ data }) => {
          const list = Array.isArray(data) ? data : [];
          setRecipientCandidates(list);
          const pre = Array.isArray(context?.preselected_recipient_keys)
            ? context.preselected_recipient_keys.filter((k) => list.some((r) => r.key === k || `${r.key}:zhd` === k))
            : null;
          if (pre && pre.length > 0) {
            setSelectedRecipients(pre);
          } else {
            setSelectedRecipients(list.filter((r) => r.default).map((r) => r.key));
          }
        })
        .catch(() => { setRecipientCandidates([]); setSelectedRecipients([]); });
    } else if (context?.vorgang_id) {
      // 2026-08 (iter104) — Schreiben aus einem Vorgang heraus: Beteiligte
      // als Empfänger-Kandidaten anbieten (Mieter, Eigentümer, Dienstleister etc.)
      api.get(`/vorgaenge/${context.vorgang_id}/recipients`)
        .then(({ data }) => {
          const list = Array.isArray(data) ? data : [];
          setRecipientCandidates(list);
          // 2026-02 — Wenn im Vorgang-Detail eine Vorabauswahl getroffen wurde,
          // diese als Selektion übernehmen; sonst wie bisher die Defaults.
          const pre = Array.isArray(context?.preselected_recipient_keys)
            ? context.preselected_recipient_keys.filter((k) => list.some((r) => r.key === k || `${r.key}:zhd` === k))
            : null;
          if (pre && pre.length > 0) {
            setSelectedRecipients(pre);
          } else {
            setSelectedRecipients(list.filter((r) => r.default).map((r) => r.key));
          }
        })
        .catch(() => { setRecipientCandidates([]); setSelectedRecipients([]); });
    } else if (context?.damage_id) {
      // 2026-02 — Schreiben aus Schaden heraus: Empfänger-Kandidaten aus dem
      // Schaden laden (Meldender, Mieter, Eigentümer, Handwerker). Zusätzlich
      // wird „Unser Zeichen" aus der Schadennummer prefilled.
      api.get(`/damages/${context.damage_id}/recipients`)
        .then(({ data }) => {
          const list = Array.isArray(data) ? data : [];
          setRecipientCandidates(list);
          setSelectedRecipients(list.filter((r) => r.default).map((r) => r.key));
        })
        .catch(() => { setRecipientCandidates([]); setSelectedRecipients([]); });
      // Schadennummer als „Unser Zeichen"
      api.get(`/damages/${context.damage_id}`)
        .then(({ data }) => {
          if (data?.damage_number) setMyReference(data.damage_number);
        })
        .catch(() => {});
    } else if (context?.unit_id) {
      // 2026-09 — Schreiben aus einer (WEG-)Einheit: Eigentümer + Miteigentümer
      api.get(`/properties/units/${context.unit_id}/recipients`)
        .then(({ data }) => {
          const list = Array.isArray(data) ? data : [];
          setRecipientCandidates(list);
          setSelectedRecipients(list.filter((r) => r.default).map((r) => r.key));
        })
        .catch(() => { setRecipientCandidates([]); setSelectedRecipients([]); });
    } else if (context?.property_id) {
      // Schreiben aus dem Objekt: alle Eigentümer als Empfänger anbieten
      api.get(`/properties/${context.property_id}/recipients`)
        .then(({ data }) => {
          const list = Array.isArray(data) ? data : [];
          setRecipientCandidates(list);
          setSelectedRecipients(list.filter((r) => r.default).map((r) => r.key));
        })
        .catch(() => { setRecipientCandidates([]); setSelectedRecipients([]); });
    } else {
      setRecipientCandidates([]); setSelectedRecipients([]);
    }

    // P1 — E-Mail-Adressen für Auswahl beim Versand laden (Kontakt oder MV)
    (async () => {
      try {
        let cids = [];
        let tenancyDoc = null;
        if (context?.contact_id) cids = [context.contact_id];
        if (context?.tenancy_id) {
          const t = await api.get(`/tenancies/${context.tenancy_id}`);
          tenancyDoc = t.data || null;
          // iter159m — Bugfix: Bei Mietverhältnissen liegen die Mieter-Kontakte
          // unter `tenant_contact_ids`. Der ältere Fallback `contact_ids` bleibt
          // erhalten (Backwards-Compat), damit Legacy-Endpoints weiter funktionieren.
          const tenantCids = Array.isArray(t.data?.tenant_contact_ids) ? t.data.tenant_contact_ids : [];
          const legacyCids = Array.isArray(t.data?.contact_ids) ? t.data.contact_ids : [];
          for (const cid of [...tenantCids, ...legacyCids]) {
            if (cid && !cids.includes(cid)) cids.push(cid);
          }
        }
        // 2026-02 Bugfix — Schreiben aus einem Vorgang: E-Mails aus den
        // Beteiligten-Kontakten laden, damit der Versand nicht fehlschlägt
        // („E-Mail konnte nicht geladen werden") wenn keine tenancy/contact_id
        // im Context steht.
        if (context?.vorgang_id && !context?.contact_id && !context?.tenancy_id) {
          try {
            const { data } = await api.get(`/vorgaenge/${context.vorgang_id}/recipients`);
            for (const r of data || []) {
              if (r?.contact_id && !cids.includes(r.contact_id)) cids.push(r.contact_id);
            }
          } catch { /* Empfänger-Liste fehlt — dann bleiben cids leer */ }
        }
        // 2026-07 — Auto-Unterbetreff bei Mietverhältnis: „Ihr Mietverhältnis: <Vertragsnr.>"
        // 2026-02 (feb) — Einheitsbezeichnung entfernt (Nutzeranforderung).
        if (tenancyDoc) {
          const nr = tenancyDoc.contract_number || tenancyDoc.tenancy_number || "";
          if (nr && !initialSubSubject && !subSubjectFromTpl.current) {
            setSubSubject(`Ihr Mietverhältnis: ${nr}`);
          }
        }
        const all = [];
        // iter159m — Pro Kontakt die primäre E-Mail sammeln (eine pro Mieter),
        // damit bei mehreren Mietern JEDER seine E-Mail automatisch bekommt.
        const primaryPerContact = [];
        for (const cid of cids) {
          try {
            const { data } = await api.get(`/contacts/${cid}`);
            const emails = data?.emails || [];
            // Vollständige Liste aller Adressen dieses Kontakts einsammeln
            for (const em of emails) {
              const norm = typeof em === "string" ? { email: em, label: null, is_primary: false } : em;
              if (norm.email && !all.some((e) => e.email === norm.email)) all.push(norm);
            }
            // Primäre (oder erste) Adresse dieses Kontakts merken
            const normalized = emails.map((em) => (typeof em === "string" ? { email: em, is_primary: false } : em));
            const primary = normalized.find((e) => e.is_primary) || normalized[0];
            if (primary?.email && !primaryPerContact.includes(primary.email)) {
              primaryPerContact.push(primary.email);
            }
          } catch { /* Kontakt konnte nicht geladen werden — ignorieren */ }
        }
        setAvailableEmails((cur) => [...all, ...cur.filter((c) => c.label === "Eigentümer" && !all.some((a) => a.email === c.email))]);
        // iter159m — Wenn defaultEmail explizit übergeben wurde, respektiere sie.
        // Sonst: alle primären Mieter-Adressen vorwählen (eine pro Mieter).
        if (defaultEmail) {
          setEmailToList([defaultEmail]);
          setEmailOverride("");
        } else if (primaryPerContact.length > 0) {
          setEmailToList(primaryPerContact);
          setEmailOverride("");
        } else if (all.length > 0) {
          const fallback = all.find((e) => e.is_primary) || all[0];
          setEmailToList([fallback.email]);
          setEmailOverride("");
        } else {
          setEmailToList([]);
          // Nur wenn gar kein Chip verfügbar ist, wird defaultEmail als Fallback gesetzt
          setEmailOverride(defaultEmail || "");
        }
        // P2 — Kontakt-Rollen für Auswahl beim Anschreiben sammeln
        const roleSet = new Set();
        for (const cid of cids) {
          try {
            const { data } = await api.get(`/contacts/${cid}`);
            (data?.roles || []).forEach((r) => roleSet.add(r));
          } catch { /* Kontakt konnte nicht geladen werden — ignorieren */ }
        }
        const roles = Array.from(roleSet);
        setContactRoleOptions(roles);
        // Wenn genau eine Rolle → automatisch vorwählen (keine Nachfrage nötig)
        if (roles.length === 1) setContactRole(roles[0]);
        else if (context?.contact_role || context?.role) setContactRole(context.contact_role || context.role);
        else setContactRole("");
      } catch { setAvailableEmails([]); }
    })();
    api.get("/users/me")
      .then(({ data }) => {
        setHasSignature(!!(data?.signature_block && data.signature_block.trim()));
        setUserFont({
          family: data?.editor_font_family || "",
          size: data?.editor_font_size ? String(data.editor_font_size) : "",
        });
      })
      .catch(() => setHasSignature(false));
    if (editor) {
      // VM-9e — Editor beginnt mit Platzhalter; der useEffect ersetzt ihn
      // synchron beim Öffnen und danach live bei jeder Chip-Änderung.
      // 2026-10 · Wenn `initialBody` gesetzt ist (z. B. Ratenplan-Erinnerung),
      // vor die Grußzeile einfügen — der User kann alles editieren.
      const bodyHtml = initialBody && initialBody.trim().startsWith("<")
        ? initialBody
        : initialBody
        ? initialBody
            .split(/\n\n+/)
            .map((para) => `<p>${para.replace(/\n/g, "<br/>")}</p>`)
            .join("")
        : "";
      editor.commands.setContent(
        bodyHtml || "<p>{{recipient.greeting}}</p><p></p><p></p>",
        false,
      );
    }
    // Phase VM-3 — Vorgänge für aktuellen Kontext laden
    // 2026-07 — Falls Kontext bereits einen Vorgang mitliefert (z. B.
    // Vorgang-Detail → „Schreiben verfassen"), gleich in den Vorgangs-Modus
    // schalten und die ID vorbelegen. „Unser Zeichen" wird durch den
    // separaten useEffect (vorgangId-Watcher) automatisch aus dem Vorgang gezogen.
    if (context?.vorgang_id) {
      setLetterMode("vorgang");
      setVorgangId(context.vorgang_id);
    } else {
      setLetterMode("standard");
      setVorgangId("");
    }
    (async () => {
      const params = {};
      if (context?.tenancy_id) params.tenancy_id = context.tenancy_id;
      else if (context?.property_id) params.property_id = context.property_id;
      else if (context?.contact_id) params.owner_contact_id = context.contact_id;
      // 2026-07 — Wenn vorgang_id im Kontext gesetzt ist, laden wir ihn direkt
      // per ID, damit er im Dropdown sichtbar ist — auch wenn KEIN
      // Bezugs-Kontext (tenancy/property/contact) an den Composer übergeben wurde.
      if (context?.vorgang_id) {
        try {
          const { data } = await api.get(`/vorgaenge/${context.vorgang_id}`);
          if (data) {
            setVorgaenge([data]);
            return;
          }
        } catch { /* ignore, fällt auf leer zurück */ }
      }
      if (!params.tenancy_id && !params.property_id && !params.owner_contact_id) {
        setVorgaenge([]);
        return;
      }
      try {
        const { data } = await api.get("/vorgaenge", { params });
        // Nur offene Vorgänge anbieten (kein archiviert/erledigt)
        setVorgaenge((data || []).filter((v) => !["archiviert", "erledigt"].includes(v.status)));
      } catch (err) {
        setVorgaenge([]);
        // Nicht-blockierend melden, damit User weiß, warum "Vorgangsbezogen" ggf. disabled ist
        console.warn("[MessagingDialog] Vorgänge konnten nicht geladen werden:", err?.message || err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Phase VM-3 — Wenn Vorgang gewählt: "Unser Zeichen" automatisch aus dem Vorgang
  //   iter137 — auch dann greifen, wenn letterMode nicht 'vorgang' ist,
  //   solange der Nutzer aktiv einen Vorgang zugeordnet hat.
  useEffect(() => {
    if (!vorgangId) return;
    const v = vorgaenge.find((x) => x.id === vorgangId);
    if (v?.unser_zeichen) setMyReference(v.unser_zeichen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vorgangId, letterMode, vorgaenge.length]);

  // VM-9 / VM-9e — Live-Anrede: sobald sich Empfänger (Chips) oder Kontext
  // ändern, wird die Anrede-Zeile im Editor neu berechnet und ersetzt.
  // Heuristik: die erste <p> im Editor gilt als Anrede-Zeile, wenn sie den
  // Platzhalter `{{recipient.greeting}}` enthält ODER mit einem "geehrt"-
  // Text beginnt (auch nach vorherigen Refreshes). Kein Custom-Attribut nötig
  // — TipTap würde `data-*` beim setContent stripen.
  useEffect(() => {
    if (!open || !editor) return;
    const hasContext = context?.tenancy_id || context?.contact_id || context?.property_id
      || (selectedRecipients && selectedRecipients.length > 0);
    if (!hasContext) return;
    const current = editor.getHTML() || "";
    // Erste <p>…</p> extrahieren
    const firstPMatch = current.match(/<p[^>]*>([\s\S]*?)<\/p>/);
    if (!firstPMatch) return;
    const firstPInner = firstPMatch[1] || "";
    const looksLikeGreeting =
      firstPInner.includes("{{recipient.greeting}}")
      || /geehrt/i.test(firstPInner)
      || /damen und herren/i.test(firstPInner);
    if (!looksLikeGreeting) return;
    (async () => {
      try {
        const { data } = await api.post("/messages/preview", {
          subject: "",
          body: "{{recipient.greeting}}",
          is_html: true,
          recipient_keys: selectedRecipients?.length ? selectedRecipients : undefined,
          address_from_contact_id: addressFromContactId || undefined,
          ...context,
        });
        const rendered = (data?.body || "").trim();
        if (!rendered) return;
        // Inhalt aus dem Response-<p>…</p> extrahieren
        const inner = rendered.replace(/^<p[^>]*>|<\/p>\s*$/g, "");
        if (!inner || inner === firstPInner) return;  // nichts zu tun
        // Ersten Paragraphen im Editor durch neuen Inhalt ersetzen
        const nextHtml = current.replace(firstPMatch[0], `<p>${inner}</p>`);
        if (nextHtml !== current) editor.commands.setContent(nextHtml, false);
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedRecipients.join(","), addressFromContactId, context?.tenancy_id, context?.contact_id]);

  // iter140 — Lade Adressen der ausgewählten Empfänger-Kontakte, damit wir bei
  // unterschiedlichen Privatadressen einen Selector anzeigen können.
  useEffect(() => {
    if (!open || selectedRecipients.length < 2) {
      setRecipientAddresses([]);
      return;
    }
    // eindeutige contact_ids (recipient key = "<contact_id>[:mode[:...]]")
    const contactIds = Array.from(new Set(
      selectedRecipients.map((k) => (k || "").split(":")[0]).filter(Boolean),
    ));
    if (contactIds.length < 2) { setRecipientAddresses([]); return; }
    Promise.all(contactIds.map((cid) => api.get(`/contacts/${cid}`).then((r) => r.data).catch(() => null)))
      .then((rows) => {
        const items = rows.filter(Boolean).map((c) => {
          const a = c.address || {};
          const parts = [
            [a.street, a.house_number].filter(Boolean).join(" "),
            [a.zip, a.city].filter(Boolean).join(" "),
          ].filter(Boolean);
          return {
            id: c.id,
            name: (c.first_name || c.last_name) ? `${c.first_name || ""} ${c.last_name || ""}`.trim() : (c.company_name || "(unbenannt)"),
            addressLine: parts.join(", ") || "(keine Adresse hinterlegt)",
          };
        });
        // Nur anzeigen wenn Adressen tatsächlich unterschiedlich sind
        const uniq = new Set(items.map((i) => i.addressLine));
        setRecipientAddresses(uniq.size > 1 ? items : []);
        // Falls das aktuell gewählte contactId nicht mehr in der Liste → zurücksetzen
        if (addressFromContactId && !items.some((i) => i.id === addressFromContactId)) {
          setAddressFromContactId("");
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedRecipients.join(",")]);

  // Eigentümer-Empfänger (Mietverhältnis/Objekt): deren E-Mail-Adressen zur Auswahl anbieten
  useEffect(() => {
    const owners = recipientCandidates.filter((r) => r.role === "owner" && r.type !== "contact_person");
    if (!open || !owners.length) return;
    let cancelled = false;
    (async () => {
      const found = [];
      const preselect = [];
      for (const o of owners) {
        try {
          const { data } = await api.get(`/contacts/${o.contact_id}`);
          const ems = (data?.emails || []).map((em) => (typeof em === "string" ? { email: em, is_primary: false } : em)).filter((e) => e.email);
          found.push(...ems.map((e) => ({ ...e, label: e.label || "Eigentümer" })));
          const p = ems.find((e) => e.is_primary) || ems[0];
          if (o.default && p) preselect.push(p.email);
        } catch { /* ignorieren */ }
      }
      if (cancelled) return;
      setAvailableEmails((cur) => [...cur, ...found.filter((f) => !cur.some((c) => c.email === f.email))]);
      if (!context?.tenancy_id && preselect.length) setEmailToList((cur) => (cur.length ? cur : preselect));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recipientCandidates]);

  const applyTemplate = async (tpl) => {
    if (!tpl) return;
    if (editor) {
      // Editor als "leer" behandeln, wenn nur das Default-Greeting-Markup drinsteht
      const current = editor.getHTML() || "";
      const normalized = current.replace(/\s+/g, "");
      const isEmpty = !current || normalized === "<p></p>" || normalized === "<p>{{recipient.greeting}}</p><p></p><p></p>";
      if (!isEmpty && !await window._domivusConfirm("Der Editor enthält bereits Text. Vorlage ersetzt den kompletten Inhalt — fortfahren?")) {
        return;
      }
      // 2026-07 — Anrede-Duplikat-Fix beim Einfügen einer Vorlage:
      // 1) Aus dem Vorlagen-Body ALLE führenden Absätze entfernen, die wie
      //    eine Anrede aussehen (statisch „Sehr geehrte …" ODER Macro
      //    `{{recipient.greeting}}`). Damit vermeiden wir Duplikate wenn
      //    der Templatetext bereits eine Anrede enthält.
      // 2) `{{recipient.greeting}}` als ersten Absatz voranstellen. Der
      //    Live-Anrede-useEffect ersetzt das Macro anschließend automatisch
      //    mit der korrekten Anrede für den/die aktuell gewählten Empfänger.
      let cleaned = String(tpl.body || "");
      // Führende leere Absätze (auch <br>/&nbsp;) entfernen
      const LEADING_EMPTY = /^(\s*<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+/i;
      cleaned = cleaned.replace(LEADING_EMPTY, "");
      // Bis zu 2 führende Anrede-Paragraphen entfernen (Anrede + optional Leerzeile)
      for (let i = 0; i < 2; i++) {
        cleaned = cleaned.replace(
          /^\s*<p[^>]*>\s*(?:\{\{\s*recipient\.greeting\s*\}\}\s*[.,!]?|(?:sehr\s+geehrt|guten\s+tag|hallo|liebe)[^<]{0,200}?)\s*<\/p>/i,
          "",
        );
      }
      // Wieder führende Leerzeilen abschneiden
      cleaned = cleaned.replace(LEADING_EMPTY, "");
      const withGreeting = `<p>{{recipient.greeting}}</p>${cleaned || "<p></p>"}`;
      editor.commands.setContent(withGreeting);
    }
    if (tpl.subject) setSubject(tpl.subject);
    if ((tpl.sub_subject || "").trim()) { subSubjectFromTpl.current = true; setSubSubject(tpl.sub_subject); }
    setCategory(tpl.category || "Schriftverkehr");
    toast.success(`Vorlage „${tpl.name}" geladen — Anrede wird automatisch aktualisiert`);
  };

  const insertAtCursor = (html) => {
    if (!editor || !html) return;
    editor.chain().focus().insertContent(html).run();
  };

  // VM-12 — Auto-Save-Draft: sobald User zu tippen beginnt, wird nach 3s
  // Inaktivität ein Entwurf angelegt und danach idempotent aktualisiert.
  useEffect(() => {
    if (!open || !editor) return;
    // Reset beim Öffnen
    setDraftId(null);
    setDraftSavedIso(null);
    setDraftSaving(false);
    // 2026-08 — Wenn ein bestehender Draft weiterbearbeitet wird (aus Dashboard-Widget),
    // laden wir Betreff/Body und markieren die Draft-ID.
    if (context?.draft_id) {
      api.get(`/messages/drafts/${context.draft_id}`)
        .then(({ data }) => {
          if (!data) return;
          setDraftId(data.id);
          setSubject(data.subject || "");
          setSubSubject(data.sub_subject || "");
          setMyReference(data.my_reference || "");
          setTheirReference(data.their_reference || "");
          setEmailOverride(data.email_to || "");
          setCategory(data.category || "Schriftverkehr");
          if (data.channels?.length) {
            setChannels({
              email: data.channels.includes("email"),
              post: data.channels.includes("post"),
            });
          }
          if (data.body && editor) editor.commands.setContent(data.body);
          if (data.attachment_document_ids?.length) setAttachmentDocIds(data.attachment_document_ids);
          setDraftSavedIso(data.updated_at || null);
        })
        .catch(() => { /* ignore */ });
    }
  }, [open, editor, context?.draft_id]);

  useEffect(() => {
    if (!open || !editor) return;
    let timer = null;
    const scheduleSave = () => {
      clearTimeout(timer);
      // iter157 — Debounce von 1200 ms → 500 ms, damit Text nicht verloren
      // geht wenn der Nutzer schnell nach dem Tippen wechselt/schließt.
      timer = setTimeout(() => saveDraftNow({ silent: true }), 500);
    };
    const handler = () => scheduleSave();
    // iter157 — Zusätzlich beim Verlassen des Editors (blur) sofort speichern
    // — wenn der Nutzer z. B. den Fokus in ein Betreff-Feld verschiebt, ist
    // der Text sicher, auch bevor der Debounce ausläuft.
    const onBlur = () => { clearTimeout(timer); saveDraftNow({ silent: true }); };
    editor.on("update", handler);
    editor.on("blur", onBlur);
    // Auch Betreff/Kontext-Änderungen triggern Save
    scheduleSave();
    return () => {
      clearTimeout(timer);
      editor.off("update", handler);
      editor.off("blur", onBlur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editor, subject, subSubject, selectedRecipients.join(","), vorgangId,
      context?.tenancy_id, context?.contact_id, myReference, theirReference,
      emailOverride, category]);

  // 2026-02 (iter120) — Einheitlicher Save-Draft-Helper.
  // Wird sowohl vom Auto-Save als auch vom expliziten
  // „Als Entwurf speichern"-Button verwendet.
  // iter157 — Vereinfacht: sobald der Editor Text hat ODER ein Betreff/
  // Empfänger vorhanden ist, wird gespeichert. Kein zu strikter
  // „substantial"-Check mehr, damit auch kurze Notizen nicht verloren gehen.
  const saveDraftNow = async ({ silent = false } = {}) => {
    if (!editor) return null;
    const body = editor.getHTML() || "";
    // Speichern bereits ab minimalem Inhalt: verhindert Datenverlust bei
    // knappen Notizen, kurzen Betreffs (< 3 Zeichen) oder wenn der Nutzer
    // erst später einen Betreff eingibt.
    const bodyText = body
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/{{recipient\.greeting}}/g, "")
      .trim();
    const hasAnyInput = bodyText.length > 0
      || (subject || "").trim().length > 0
      || (subSubject || "").trim().length > 0
      || attachmentDocIds.length > 0
      || selectedRecipients.length > 0
      || (myReference || "").trim().length > 0
      || (theirReference || "").trim().length > 0;
    if (!hasAnyInput) {
      if (!silent) toast.info("Kein Inhalt zum Speichern.");
      return null;
    }
    try {
      setDraftSaving(true);
      const payload = {
        subject, sub_subject: subSubject, body, is_html: true,
        channels: [],
        tenancy_id: context?.tenancy_id, contact_id: context?.contact_id,
        property_id: context?.property_id,
        vorgang_id: letterMode === "vorgang" && vorgangId ? vorgangId : null,
        my_reference: myReference || null, their_reference: theirReference || null,
        recipient_keys: selectedRecipients.length ? selectedRecipients : null,
          address_from_contact_id: addressFromContactId || undefined,
        email_to: emailOverride || null, category,
        attachment_document_ids: attachmentDocIds,
        rent_increase_request_id: context?.rent_increase_request_id || null,
      };
      if (draftId) {
        try {
          const params = draftSavedIso ? { expected_updated_at: draftSavedIso } : {};
          const { data } = await api.put(`/messages/drafts/${draftId}`, payload, { params });
          if (data?.updated_at) setDraftSavedIso(data.updated_at);
          if (!silent) toast.success("Entwurf gespeichert");
          return data;
        } catch (err) {
          if (err?.response?.status === 409) {
            const d = err.response.data?.detail || {};
            setConflict({
              other_user_name: d.other_user_name || null,
              current_updated_at: d.current_updated_at || null,
            });
            return null;
          }
          throw err;
        }
      } else {
        const { data } = await api.post("/messages/drafts", payload);
        setDraftId(data.id);
        if (data?.updated_at) setDraftSavedIso(data.updated_at);
        if (!silent) toast.success("Entwurf gespeichert");
        return data;
      }
    } catch (e) {
      if (!silent) toast.error(formatApiError(e));
      return null;
    } finally {
      setDraftSaving(false);
    }
  };

  // iter157 — Vor dem Schließen des Tabs / Reload: letzten Stand versuchen
  // zu speichern (best-effort mit fetch + keepalive, damit auch bei
  // tab-close nichts verloren geht; sendBeacon wäre einfacher, kann aber
  // keine Auth-Header setzen).
  useEffect(() => {
    if (!open) return;
    const onBeforeUnload = () => {
      if (!editor || !draftId) return;
      const body = editor.getHTML() || "";
      const payload = {
        subject, sub_subject: subSubject, body, is_html: true, channels: [],
        tenancy_id: context?.tenancy_id, contact_id: context?.contact_id,
        property_id: context?.property_id,
        vorgang_id: letterMode === "vorgang" && vorgangId ? vorgangId : null,
        my_reference: myReference || null, their_reference: theirReference || null,
        recipient_keys: selectedRecipients.length ? selectedRecipients : null,
        email_to: emailOverride || null, category,
      };
      try {
        const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/drafts/${draftId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      } catch { /* egal */ }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [open, editor, draftId, subject, subSubject, myReference, theirReference,
      emailOverride, selectedRecipients, category, letterMode, vorgangId,
      context?.tenancy_id, context?.contact_id, context?.property_id]);

  // 2026-08 (iter111) — Live-Presence-Heartbeat: alle 15s pingen, damit andere
  // Nutzer sehen, dass wir gerade tippen. Antwort liefert auch, wer sonst am
  // Draft arbeitet.
  useEffect(() => {
    if (!open || !draftId) { setActiveEditor(null); return; }
    let alive = true;
    const ping = async () => {
      try {
        const { data } = await api.post(`/messages/drafts/${draftId}/heartbeat`);
        if (!alive) return;
        if (data?.is_me) {
          setActiveEditor(null);
        } else if (data?.active_editor_name) {
          setActiveEditor({
            name: data.active_editor_name,
            since: data.active_editor_since,
          });
        } else {
          setActiveEditor(null);
        }
      } catch { /* silent */ }
    };
    ping();
    const iv = setInterval(ping, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, [open, draftId]);

  // 2026-08 — hasEditorContent: Prüft, ob substantieller Inhalt existiert
  // (Betreff, sub_subject, Empfänger, ODER Editor-Body nicht leer).
  // Der Editor startet mit "<p>{{recipient.greeting}}</p><p></p><p></p>" —
  // dieser Standard-Zustand zählt NICHT als „geschrieben".
  const editorLooksEmpty = (html) => {
    if (!html) return true;
    const stripped = html
      .replace(/<p[^>]*>\s*<\/p>/g, "")
      .replace(/{{recipient\.greeting}}/g, "")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();
    return stripped.length === 0;
  };
  const hasEditorContent = () => {
    // Auto-Default-Betreff („Unser Schreiben v. …") zählt NICHT als „geschrieben"
    const subjTrim = (subject || "").trim();
    if (subjTrim && subjTrim !== (autoDefaultSubject || "").trim()) return true;
    if (subSubject.trim()) return true;
    if (myReference?.trim() || theirReference?.trim()) return true;
    if (emailOverride?.trim()) return true;
    if (attachmentDocIds?.length > 0) return true;
    if (selectedRecipients?.length > 0) return true;
    return editor && !editorLooksEmpty(editor.getHTML() || "");
  };

  // Zentraler Handler für Dialog-Schließ-Versuche (ESC / Klick daneben / X)
  // 2026-02 Bugfix: Wenn ein Kind-Dialog (PDF-Vorschau, Snippet-Picker, Send-Modal,
  // Missing-Subject-Prompt, Confirm-Exit) offen ist, feuert Radix onInteractOutside/
  // onEscapeKeyDown auch auf den Editor-Dialog – das ließ „Editor verlassen?" beim
  // Öffnen der PDF-Vorschau erscheinen und man kam nicht zurück.
  const isChildDialogOpen = () => {
    if (pdfPreviewOpen || snippetOpen || sendModalOpen || missingSubjectPrompt || confirmExit) return true;
    // 2026-02 (feb) — Globaler Confirm-Dialog (ConfirmProvider) offen?
    // Klick auf dessen Buttons feuert Radix `onInteractOutside` beim
    // MessagingDialog — ohne diesen Check zeigt der Editor fälschlich
    // „Als Entwurf speichern?" beim Einfügen einer Vorlage.
    if (typeof document !== "undefined" &&
        (document.querySelector('[data-testid="global-confirm-dialog"]')
         || document.querySelector("[data-radix-popper-content-wrapper]"))) {
      return true;
    }
    return false;
  };
  const requestClose = async () => {
    if (justSentRef.current) { onOpenChange(false); return; }
    if (isChildDialogOpen()) return;  // Klick war für Kind-Dialog, nicht schließen
    if (hasEditorContent()) {
      // iter134 — Vor der „wirklich schließen?"-Rückfrage synchron flushen,
      // damit der aktuellste Betreff / Body auch im Draft persistiert wird,
      // selbst wenn der Auto-Save-Debounce noch nicht abgelaufen ist.
      try { await saveDraftNow({ silent: true }); } catch { /* egal */ }
      setConfirmExit(true);
    } else {
      onOpenChange(false);
    }
  };
  const confirmDiscardAndClose = async () => {
    // Optional: aktuellen (leeren-behandelten) Draft löschen, damit kein Müll entsteht
    if (draftId && !hasEditorContent()) {
      try { await api.delete(`/messages/drafts/${draftId}`); } catch { /* egal */ }
    }
    setConfirmExit(false);
    onOpenChange(false);
  };

  const handlePreview = async () => {
    try {
      const body = editor?.getHTML() || "";
      const { data } = await api.post("/messages/preview", {
        subject,
        sub_subject: subSubject,
        body,
        is_html: true,
        my_reference: myReference || undefined,
        their_reference: theirReference || undefined,
        recipient_keys: selectedRecipients.length ? selectedRecipients : undefined,
          address_from_contact_id: addressFromContactId || undefined,
        vorgang_id: letterMode === "vorgang" && vorgangId ? vorgangId : undefined,
        ...context,
      });
      setPreview(data);
      return data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  // Phase 21β-4 — Inline-Vorschau ein/aus: lädt gerendertes HTML in previewEditor
  const toggleLivePreview = async () => {
    if (livePreview) {
      // Vorschau wieder ausschalten — der bearbeitete Stand der Vorschau wird
      // beim nächsten Versand verwendet (siehe handleSend).
      setLivePreview(false);
      return;
    }
    setPreviewLoading(true);
    const data = await handlePreview();
    if (data && previewEditor) {
      previewEditor.commands.setContent(data.body || "", false);
      setLivePreview(true);
    }
    setPreviewLoading(false);
  };

  // VM-7c — In-App PDF-Vorschau (kein window.open beim Klick, um Popup-Blocker zu umgehen)
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState("");

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    if (pdfPreviewUrl) {
      try { URL.revokeObjectURL(pdfPreviewUrl); } catch { /* noop */ }
      setPdfPreviewUrl("");
    }
    setPdfPreviewError("");
  };

  const handleDownloadPreviewPdf = async () => {
    // VM-7c — Popup-Blocker-Umgehung: Wir öffnen KEIN externes Tab, sondern eine
    // In-App-Modal mit iframe. Das läuft in jedem Browser und jeder WebView zuverlässig.
    setPdfPreviewOpen(true);
    setPdfPreviewLoading(true);
    setPdfPreviewError("");
    if (pdfPreviewUrl) {
      try { URL.revokeObjectURL(pdfPreviewUrl); } catch { /* noop */ }
      setPdfPreviewUrl("");
    }
    try {
      const body = (livePreview && previewEditor ? previewEditor.getHTML() : editor?.getHTML()) || "";
      const selectedChannels = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
      const payload = {
        subject,
        sub_subject: subSubject,
        body, is_html: true,
        // Channels nur für die „per E-Mail"-Zeile mitgeben (kein Versand!)
        channels: selectedChannels.length ? selectedChannels : ["post"],
        email_to: channels.email && emailOverride ? emailOverride : undefined,
        category,
        my_reference: myReference || undefined,
        their_reference: theirReference || undefined,
        letterhead_id: letterheadId || undefined,
        recipient_keys: selectedRecipients.length ? selectedRecipients : undefined,
          address_from_contact_id: addressFromContactId || undefined,
        vorgang_id: letterMode === "vorgang" && vorgangId ? vorgangId : undefined,
        ...context,
      };
      // iter137 — api.post statt raw fetch, damit der axios-Interceptor
      // abgelaufene Tokens transparent per Refresh erneuert (kein
      // „Token abgelaufen" im Preview-Modal mehr).
      const { data: blob } = await api.post("/messages/preview-pdf", payload, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Fehler beim Erzeugen der Vorschau";
      setPdfPreviewError(detail);
    } finally {
      setPdfPreviewLoading(false);
    }
  };

  const handleSend = async (forceDefaultSubject = false) => {
    const selected = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    if (selected.length === 0) { toast.error("Bitte mindestens einen Versandweg wählen."); return; }
    if (!context?.tenancy_id && !context?.contact_id && !context?.property_id
        && !(selectedRecipients && selectedRecipients.length > 0)) {
      toast.error("Empfängerkontext fehlt."); return;
    }
    // P2 — Rollen-Auswahl bei mehreren Rollen des Empfänger-Kontakts
    let effectiveRole = contactRole;
    if (!effectiveRole && contactRoleOptions.length > 1) {
      const list = contactRoleOptions
        .map((r, i) => `${i + 1}. ${CONTACT_ROLES[r] || r}`)
        .join("\n");
      const choice = window.prompt(
        `Der Empfänger hat mehrere Rollen. In welcher Rolle wird angeschrieben?\n\n${list}\n\nBitte Nummer eingeben:`,
        "1"
      );
      if (!choice) { toast.info("Versand abgebrochen – bitte Rolle des Empfängers wählen."); return; }
      const idx = parseInt(choice, 10) - 1;
      if (idx < 0 || idx >= contactRoleOptions.length) {
        toast.error("Ungültige Auswahl."); return;
      }
      effectiveRole = contactRoleOptions[idx];
      setContactRole(effectiveRole);
    }
    // 2026-02 — Standard-Titel-Prüfung: wenn Betreff leer → inline Dialog (kein Browser-Popup)
    let effectiveSubject = subject;
    let defaultTitleUsed = false;
    if (!effectiveSubject || !effectiveSubject.trim()) {
      // Wenn wir aus dem Bestätigungs-Dialog kommen, ist forceDefaultSubject=true
      if (!forceDefaultSubject) {
        setMissingSubjectPrompt(true);
        return;
      }
      effectiveSubject = "Schreiben";
      defaultTitleUsed = true;
    }
    setSending(true);
    try {
      // 2026-07 — Bei Rundschreiben MUSS der Original-Editor-Body (mit rohen Makros
      // wie {{recipient.greeting}}) verwendet werden, damit das Backend pro Empfänger
      // die korrekte Anrede rendert. Der Preview-Editor hat die Makros bereits mit
      // dem primären Empfänger aufgelöst — würde für alle die gleiche Anrede setzen.
      const isRundschreiben = rundschreiben && wizardRecipients && wizardRecipients.length > 1 && rundschreibenMode === "per_empfaenger";
      const body = isRundschreiben
        ? (editor?.getHTML() || "")
        : ((livePreview && previewEditor ? previewEditor.getHTML() : editor?.getHTML()) || "");
      const basePayload = {
        subject: effectiveSubject,
        sub_subject: subSubject,
        body, is_html: true,
        channels: selected,
        email_to: channels.email && emailOverride ? emailOverride : undefined,
        // 2026-02 — Multi-To + BCC (Backend akzeptiert beides)
        email_to_list: channels.email && emailToList.length ? emailToList : undefined,
        email_bcc: channels.email && emailBcc.length ? emailBcc : undefined,
        email_cc: channels.email && emailCc.length ? emailCc : undefined,
        // 2026-02 (iter118) — Kenntnisnahme
        requires_acknowledgment: !!requiresAcknowledgment,
        acknowledgment_deadline_days: requiresAcknowledgment ? Number(ackDeadlineDays) || null : null,
        category,
        my_reference: myReference || undefined,
        their_reference: theirReference || undefined,
        letterhead_id: letterheadId || undefined,
        vorgang_id: letterMode === "vorgang" && vorgangId ? vorgangId : undefined,
        contact_role: effectiveRole || undefined,          // P2 Auto-Dateibenennung
        filename_context: filenameContext || undefined,    // P2 Auto-Dateibenennung
        default_title_used: defaultTitleUsed,
        attachment_document_ids: attachmentDocIds.length ? attachmentDocIds : undefined,
        // 2026-08 (iter111) — Ausgewählte Ticket-Anlagen mit an die Mail hängen
        ticket_attachment_refs: (ticketAttachments || [])
          .filter((a) => ticketAttachmentSelected[a.id])
          .map((a) => ({ ticket_id: a.ticket_id, attachment_id: a.id })),
        email_template_id: emailTemplateId || undefined,
        wet_signature: wetSignature || undefined,
        signed_scan_id: wetSignature && signedScan ? signedScan.id : undefined,
        signed_scan_pending: wetSignature && !signedScan && scanPending ? true : undefined,
        wet_signers: wetSignature ? cleanSigners() : undefined,
        // Kommunikations-Journal
        in_reply_to_id: context?.in_reply_to_id || undefined,
        journal_tenancy_id: context?.journal_tenancy_id || undefined,
        follow_up_date: followUpDate || undefined,
      };
      const batchId = rundschreiben && wizardRecipients && wizardRecipients.length > 1 ? crypto.randomUUID() : undefined;

      // VM-7d — Rundschreiben: pro Empfänger ein eigenes /messages/send
      if (rundschreiben && wizardRecipients && wizardRecipients.length > 1 && rundschreibenMode === "per_empfaenger") {
        let ok = 0, fail = 0;
        for (const r of wizardRecipients) {
          try {
            const recPayload = { ...basePayload, batch_id: batchId, batch_mode: "serial" };
            if (r.tenancy_id) recPayload.tenancy_id = r.tenancy_id;
            else if (r.contact_id) recPayload.contact_id = r.contact_id;
            if (r.contact_role) recPayload.contact_role = r.contact_role;
            if (context?.vorgang_id) recPayload.vorgang_id = context.vorgang_id;
            // 2026-07 — Empfänger ohne MV (nur Einheit): unit_id + property_id mitschicken
            if (r.unit_id) recPayload.unit_id = r.unit_id;
            if (context?.property_id) recPayload.property_id = context.property_id;
            else if (r.property_id) recPayload.property_id = r.property_id;
            await api.post("/messages/send", recPayload);
            ok++;
          } catch { fail++; }
        }
        if (ok > 0) window.dispatchEvent(new CustomEvent("domivus:message-sent"));
        if (fail === 0) {
          toast.success(`Rundschreiben versendet — ${ok} Empfänger`);
          onOpenChange(false);
        } else if (ok === 0) {
          toast.error(`Rundschreiben fehlgeschlagen — 0 versendet, ${fail} Fehler`, { duration: 12000 });
          // Dialog offen lassen → Inhalt nicht verlieren
        } else {
          toast.warning(`${ok} versendet, ${fail} fehlgeschlagen`);
          onOpenChange(false);
        }
        return;
      }

      // Standard: einzelner Versand
      const payload = {
        ...basePayload,
        recipient_keys: selectedRecipients.length ? selectedRecipients : undefined,
          address_from_contact_id: addressFromContactId || undefined,
        draft_id: draftId || undefined,  // VM-12 — konsumiert Draft nach Erfolg
        ...context,
        ...(batchId ? { batch_id: batchId, batch_mode: "circular",
          journal_contact_ids: wizardRecipients.map((r) => r.contact_id).filter(Boolean) } : {}),
      };
      const { data } = await api.post("/messages/send", payload);
      // 2026-02 — Direkt-Druck für Postversand
      // Druckauswahl erst NACH dem Schließen des Editors öffnen (sonst greift der Verlassen-Dialog)
      const pendingPrint = channels.post && data?.document?.id && attachmentDocIds.length > 0
        ? { documentId: data.document.id, attachmentCount: data.document.attachment_count || attachmentDocIds.length,
            signedOriginal: wetSignature }
        : null;
      if (hasSuccessfulChannel(data.results)) { justSentRef.current = true; window.dispatchEvent(new CustomEvent("domivus:message-sent")); }
      if (pendingPrint) {
        /* Dialog folgt unten nach onOpenChange(false) */
      } else if (printAfterPost && channels.post && data?.document?.id && !wetSignature) {
        try {
          await triggerPrint(data.document.id);
        } catch (e) {
          toast.warning("Drucken nicht möglich: " + (e?.message || e));
        }
      }
      const lines = [];
      let hasFailure = false;
      let hasSuccess = false;
      if (data.results?.email) {
        const r = data.results.email;
        if (r.status === "sent") {
          hasSuccess = true;
          lines.push(`E-Mail: versendet`);
        } else if (r.status === "queued_no_provider") {
          lines.push(`E-Mail: vorgemerkt (kein Provider)`);
        } else {
          hasFailure = true;
          lines.push(`E-Mail: fehlgeschlagen – ${r.reason || ""}`);
        }
      }
      if (data.results?.post) {
        hasSuccess = true;
        lines.push(wetSignature
          ? (data?.document?.signature_status === "pending_scan"
            ? "Post: Versandbeleg gespeichert · Aufgabe „Scan nachreichen“ angelegt"
            : "Post: unterschriebenes Exemplar gespeichert")
          : "Post: bereit zum Druck (Versandbeleg gespeichert)");
      }
      // P0.4 — Wenn ALLE Kanäle fehlgeschlagen sind: Editor OFFEN lassen,
      // damit der Nutzer den Inhalt nicht verliert und den Fehler beheben kann.
      if (hasFailure && !hasSuccess) {
        toast.error("Versand fehlgeschlagen\n" + lines.join("\n"), { duration: 12000 });
        // Dialog absichtlich NICHT schließen; Editor-Inhalt bleibt erhalten.
      } else if (hasFailure && hasSuccess) {
        toast.warning("Versand teilweise erfolgreich\n" + lines.join("\n"), { duration: 10000 });
        onOpenChange(false);
      } else {
        toast.success("Nachricht gespeichert\n" + lines.join("\n"));
        onOpenChange(false);
      }
      if (pendingPrint && hasSuccess) setTimeout(() => openPostPrint(pendingPrint), 250);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  // 2026-02 — Direkt-Druck auf lokalem Drucker via Browser-Print-Dialog.
  // Lädt das Brief-PDF authentifiziert, injiziert es in ein hidden iframe
  // und ruft dessen contentWindow.print() auf.
  const triggerPrint = async (documentId, blobOverride = null) => {
    const blob = blobOverride || (await fetchPdfBlob(`/documents/${documentId}/download`)).blob;
    printPdfBlob(blob);
  };

  // Unterzeichner-Vorschläge laden, sobald „Original unterschreiben" aktiv wird
  useEffect(() => {
    if (!wetSignature || wetSigners.length) return;
    api.get("/messages/signer-suggestions", { params: {
      tenancy_id: context?.tenancy_id, contact_id: context?.contact_id,
      property_id: context?.property_id, unit_id: context?.unit_id,
    } }).then(({ data }) => {
      setSignerSuggestions(data || []);
      const me = (data || []).find((x) => x.kind === "self");
      setWetSigners([{ name: me?.name || "", role: me?.role || "" }]);
    }).catch(() => setWetSigners([{ name: "", role: "" }]));
  }, [wetSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanSigners = () => wetSigners.filter((x) => (x.name || "").trim()).slice(0, 3);

  // Original unterschreiben: PDF ohne Signaturzeilen-Füllung zum Ausdrucken
  const printForSignature = async () => {
    const body = (livePreview && previewEditor ? previewEditor.getHTML() : editor?.getHTML()) || "";
    const selectedChannels = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    try {
      const { data: blob } = await api.post("/messages/preview-pdf", {
        subject, sub_subject: subSubject, body, is_html: true,
        channels: selectedChannels.length ? selectedChannels : ["post"],
        email_to: channels.email && emailOverride ? emailOverride : undefined,
        category,
        my_reference: myReference || undefined,
        their_reference: theirReference || undefined,
        letterhead_id: letterheadId || undefined,
        recipient_keys: selectedRecipients.length ? selectedRecipients : undefined,
        address_from_contact_id: addressFromContactId || undefined,
        vorgang_id: letterMode === "vorgang" && vorgangId ? vorgangId : undefined,
        wet_signature: true,
        wet_signers: cleanSigners(),
        ...context,
      }, { responseType: "blob" });
      await triggerPrint(null, blob);
    } catch (e) {
      toast.error("Druck-PDF konnte nicht erzeugt werden: " + (e?.response?.data?.detail || e?.message || ""));
    }
  };

  // Sprint 5j — Send-Popup-State
  const [sendModalOpen, setSendModalOpen] = useState(false);
  // 2026-02 — nach Postversand direkt drucken (lokaler Drucker)
  const [printAfterPost, setPrintAfterPost] = useState(false);
  // 2026-02 — Inline-Bestätigung für fehlenden Betreff (statt Browser-Popup)
  const [missingSubjectPrompt, setMissingSubjectPrompt] = useState(false);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) requestClose(); else onOpenChange(true); }}>
      <DialogContent
        className="max-w-[99vw] w-[99vw] max-h-[96vh] overflow-hidden p-0 flex flex-col"
        data-testid="messaging-dialog"
        onEscapeKeyDown={(e) => { e.preventDefault(); requestClose(); }}
        onPointerDownOutside={(e) => { e.preventDefault(); requestClose(); }}
        onInteractOutside={(e) => { e.preventDefault(); requestClose(); }}
      >
        <DialogHeader className="px-5 pt-3 pb-2 pr-12 border-b border-slate-200 bg-white shrink-0 flex flex-row items-center justify-between">
          <div>
            <DialogTitle className="text-base">Schreiben verfassen</DialogTitle>
            <DialogDescription className="sr-only">Word-ähnlicher Editor mit Ribbon und Vorlagen</DialogDescription>
            {/* VM-12 — Draft-Status-Anzeige */}
            {(draftSaving || draftSavedIso) && (
              <div className="text-[10px] text-slate-500 mt-0.5" data-testid="draft-status">
                {draftSaving
                  ? "Entwurf wird gespeichert…"
                  : `Entwurf gespeichert um ${new Date(draftSavedIso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`}
              </div>
            )}
            {/* 2026-08 (iter111) — Live-Presence: „{Name} bearbeitet gerade (seit HH:MM)" */}
            {activeEditor && (
              <div
                className="text-[10px] text-amber-800 mt-0.5 inline-flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                data-testid="draft-live-presence"
                title="Ein anderer Nutzer bearbeitet diesen Entwurf gerade"
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-600"></span>
                </span>
                <b>{activeEditor.name}</b> bearbeitet gerade
                {activeEditor.since && (
                  <span className="text-amber-700">
                    · seit {new Date(activeEditor.since).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>
            )}
            {/* 2026-08 — Draft-Kollisions-Warnbanner */}
            {conflict && (
              <div className="mt-2 bg-amber-50 border border-amber-300 rounded-md p-2 text-xs text-amber-900 flex items-center gap-2" data-testid="draft-conflict-banner">
                <Warning size={14} weight="fill" className="text-amber-700 shrink-0" />
                <div className="flex-1">
                  <b>Achtung:</b>{" "}
                  {conflict.other_user_name
                    ? <>{conflict.other_user_name} hat diesen Entwurf zuletzt bearbeitet.</>
                    : "Ein anderer Nutzer hat diesen Entwurf parallel bearbeitet."}
                  {" "}Weiter bearbeiten kann deren Änderungen überschreiben.
                </div>
                <button
                  className="text-[11px] px-2 py-1 rounded border border-amber-400 hover:bg-amber-100"
                  onClick={async () => {
                    // Aktuelle Fassung nachladen und einspielen
                    try {
                      const { data } = await api.get(`/messages/drafts/${draftId}`);
                      if (data) {
                        setSubject(data.subject || "");
                        setSubSubject(data.sub_subject || "");
                        if (data.body && editor) editor.commands.setContent(data.body);
                        setDraftSavedIso(data.updated_at || null);
                      }
                    } catch { /* ignore */ }
                    setConflict(null);
                  }}
                  data-testid="draft-conflict-reload"
                >
                  Neu laden
                </button>
                <button
                  className="text-[11px] px-2 py-1 rounded border border-amber-400 hover:bg-amber-100"
                  onClick={() => setConflict(null)}
                  data-testid="draft-conflict-dismiss"
                >
                  Trotzdem weiter
                </button>
              </div>
            )}
          </div>
          {/* VM-7b: Mobile-Options-Button (öffnet Sidebar-Drawer) */}
          <div className="flex items-center gap-2">
            <button
              onClick={async () => { await saveDraftNow({ silent: false }); }}
              disabled={draftSaving}
              className="text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-60"
              data-testid="msg-save-draft-btn"
              title="Aktuellen Stand als Entwurf speichern (bleibt in der Entwurfsliste)"
            >
              {draftSaving ? "…" : "💾"} Als Entwurf speichern
            </button>
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="md:hidden text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 flex items-center gap-1.5"
              data-testid="msg-mobile-options-btn"
            >
              ☰ Optionen
            </button>
          </div>
        </DialogHeader>

        {/* Sprint 5j — Neues Layout: Sidebar links + Editor rechts, Ribbon oben */}
        <div className="flex-1 min-h-0 flex overflow-hidden relative">
          {/* Mobile Backdrop */}
          {mobileSidebarOpen && (
            <div
              className="md:hidden fixed inset-0 bg-black/40 z-40"
              onClick={() => setMobileSidebarOpen(false)}
              data-testid="msg-mobile-backdrop"
            />
          )}
          {/* Linke Sidebar: Empfänger, Betreff, Kategorie, Referenzen, Geschäftspapier */}
          <aside
            className={`
              border-r border-slate-200 bg-slate-50 overflow-y-auto p-4 space-y-3
              ${mobileSidebarOpen ? "fixed inset-y-0 left-0 z-50 w-[88vw] max-w-[380px] shadow-2xl" : "hidden"}
              md:relative md:flex md:flex-col md:w-[320px] md:shrink-0 md:z-auto md:shadow-none
            `}
            data-testid="msg-sidebar"
          >
            {/* Close-Button innerhalb Drawer (mobile) */}
            <div className="flex md:hidden items-center justify-between -mt-2 -mx-2 mb-1 sticky top-0 bg-slate-50 pt-2 px-2 pb-2 border-b border-slate-200">
              <div className="text-sm font-semibold text-slate-800">Optionen</div>
              <button
                onClick={() => setMobileSidebarOpen(false)}
                className="text-slate-500 hover:text-slate-800 text-lg leading-none px-2"
                data-testid="msg-mobile-close-sidebar"
              >×</button>
            </div>
            {/* VM-7d — Empfänger-Chips (immer sichtbar, auch aus Wizard) */}
            <WizardRecipientChips
              wizardRecipients={wizardRecipients}
              rundschreiben={rundschreiben}
              rundschreibenMode={rundschreibenMode}
            />
            {/* Phase VM-3 — Modus-Toggle: Standardschreiben ↔ Vorgangsbezogen */}
            <LetterModeToggle
              letterMode={letterMode}
              setLetterMode={setLetterMode}
              vorgangId={vorgangId}
              setVorgangId={setVorgangId}
              vorgaenge={vorgaenge}
            />
            {recipientCandidates.length > 0 && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Empfänger <span className="text-slate-400 font-normal normal-case">({selectedRecipients.length}/{recipientCandidates.length})</span>
                </Label>
                <div className="mt-1.5">
                  <RecipientPicker
                    candidates={recipientCandidates}
                    selected={selectedRecipients}
                    onChange={setSelectedRecipients}
                  />
                </div>
              </div>
            )}
            {/* iter142 — Hinweis, wenn MV noch nicht gestartet */}
            {(() => {
              if (!tenancyMeta) return null;
              const relevant = tenancyMeta.handover_date || tenancyMeta.contract_start;
              if (!relevant) return null;
              const today = new Date().toISOString().slice(0, 10);
              if (String(relevant) <= today) return null;
              if (tenancyMeta.termination_status === "cancelled" || tenancyMeta.termination_status === "ended") return null;
              return (
                <div className="border border-sky-300 bg-sky-50 rounded-md p-2 text-[11px] text-sky-900" data-testid="msg-future-tenancy-hint">
                  <div className="font-semibold">Mietverhältnis noch nicht gestartet</div>
                  <div className="mt-0.5">
                    Beginn: <b>{new Date(relevant).toLocaleDateString("de-DE")}</b> — Empfänger sind bis dahin unter ihrer Privatadresse zu erreichen.
                  </div>
                </div>
              );
            })()}
            {/* iter140 — Adress-Selektor bei mehreren Mietern mit unterschiedlichen Privatadressen */}
            {recipientAddresses.length > 1 && (
              <div className="border border-amber-200 bg-amber-50/60 rounded-md p-2" data-testid="msg-address-selector">
                <Label className="text-[10px] uppercase tracking-wider text-amber-900 font-semibold">
                  Anschriftfeld — Adresse verwenden von
                </Label>
                <select
                  value={addressFromContactId || recipientAddresses[0].id}
                  onChange={(e) => setAddressFromContactId(e.target.value)}
                  className="mt-1 w-full text-xs border border-amber-300 rounded px-2 py-1.5 bg-white"
                  data-testid="msg-address-from"
                >
                  {recipientAddresses.map((r, idx) => (
                    <option key={r.id} value={r.id}>
                      {idx === 0 ? "★ " : ""}{r.name} — {r.addressLine}
                    </option>
                  ))}
                </select>
                <div className="text-[10px] text-amber-800 mt-1 italic">
                  Es wurden mehrere Privatadressen erkannt. Namen aller Mieter erscheinen im Adressblock — Straße/Ort kommt vom gewählten Kontakt (Standard: erster Mieter).
                </div>
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Betreff</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="z. B. Erinnerung Mietzahlung"
                className="h-8 text-sm font-semibold mt-1"
                data-testid="msg-subject"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Unterbetreff <span className="text-slate-400 font-normal normal-case">(optional)</span>
              </Label>
              <Input
                value={subSubject}
                onChange={(e) => setSubSubject(e.target.value)}
                className="h-8 text-xs text-slate-600 mt-1"
                data-testid="msg-sub-subject"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Kategorie</Label>
              <div className="mt-1">
                <CategorySelect value={category} onChange={setCategory} testid="msg-category" />
              </div>
            </div>

            {/* 2026-02 (iter118) — Kenntnisnahme erforderlich (rechtssichere Zustellung) */}
            <div className="border border-amber-200 bg-amber-50/60 rounded-md p-2" data-testid="msg-ack-panel">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requiresAcknowledgment}
                  onChange={(e) => setRequiresAcknowledgment(e.target.checked)}
                  className="mt-0.5 accent-amber-600"
                  data-testid="msg-ack-checkbox"
                />
                <div className="text-xs flex-1">
                  <div className="font-semibold text-amber-900">Kenntnisnahme erforderlich</div>
                  <div className="text-[10px] text-amber-800 mt-0.5 leading-snug">
                    Empfänger müssen im Portal auf „Kenntnisnahme bestätigen" klicken.
                    Rechtssichere Zustelldokumentation.
                  </div>
                </div>
              </label>
              {requiresAcknowledgment && (
                <div className="mt-1.5 pl-6 flex items-center gap-1.5">
                  <Label className="text-[10px] text-amber-900 whitespace-nowrap">Frist (Tage):</Label>
                  <Input
                    type="number" min="1" max="365"
                    value={ackDeadlineDays}
                    onChange={(e) => setAckDeadlineDays(e.target.value)}
                    className="h-6 w-16 text-xs"
                    data-testid="msg-ack-days"
                  />
                  <span className="text-[10px] text-amber-800">
                    Ausstehende Bestätigungen werden nach Ablauf eskaliert
                  </span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Mein Zeichen</Label>
                <Input
                  value={myReference} onChange={(e) => setMyReference(e.target.value)}
                  placeholder={computedCode || "MR"}
                  className="h-8 font-mono text-sm mt-1" data-testid="msg-my-reference"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Ihr Zeichen</Label>
                <Input
                  value={theirReference} onChange={(e) => setTheirReference(e.target.value)}
                  placeholder="AZ 12/26"
                  className="h-8 font-mono text-sm mt-1" data-testid="msg-their-reference"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Geschäftspapier</Label>
                <a
                  href="/stammdaten?tab=letterhead"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-slate-700 hover:text-slate-900 font-medium underline"
                  data-testid="msg-letterhead-edit-link"
                  title="Geschäftspapier-Editor in neuem Tab öffnen"
                >
                  ✎ Bearbeiten
                </a>
              </div>
              <select
                value={letterheadId}
                onChange={(e) => setLetterheadId(e.target.value)}
                className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white w-full focus:outline-none focus:border-slate-400 mt-1"
                data-testid="msg-letterhead-select"
              >
                <option value="">Aktives Geschäftspapier</option>
                {letterheads.map((l) => (
                  <option key={l.id} value={l.id}>{l.label}</option>
                ))}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">Bearbeiter: {user?.name || "—"}</div>
            </div>
            <div className={`px-3 py-2 text-[11px] rounded ${hasSignature ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
              {hasSignature ? "✓ Persönliche Signatur wird angehängt." : "⚠ Keine persönliche Signatur — Mandanten-Signatur wird verwendet."}
            </div>

            {/* 2026-02 — Anhänge auswählen */}
            <div className="border-t border-slate-200 pt-3 space-y-3">
              {emailTemplates.length > 0 && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">E-Mail-Layout</label>
                  <select
                    value={emailTemplateId}
                    onChange={(e) => setEmailTemplateId(e.target.value)}
                    className="mt-1 w-full text-xs border border-slate-200 rounded px-2 py-1.5 bg-white"
                    data-testid="msg-email-template"
                  >
                    {emailTemplates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}{t.is_default ? " (Standard)" : ""}{t.is_system ? " · System" : ""}
                      </option>
                    ))}
                  </select>
                  <div className="text-[10px] text-slate-500 mt-1 italic">
                    Layouts unter <span className="font-mono">/email-vorlagen</span> verwalten.
                  </div>
                </div>
              )}
              <AttachmentPicker
                value={attachmentDocIds}
                onChange={setAttachmentDocIds}
                context={{
                  tenancy_id: context?.tenancy_id,
                  property_id: context?.property_id,
                  contact_id: context?.contact_id,
                  unit_id: context?.unit_id,
                }}
                currentUserId={user?.id}
                label="Anlagen (E-Mail & Post)"
                testid="msg-attachments"
                knownDocs={extraAttachDocs}
                extraActions={context?.tenancy_id ? (
                  <AccountStatementAttach tenancyId={context.tenancy_id} onAttached={(d) => {
                    setExtraAttachDocs((x) => [...x, d]);
                    setAttachmentDocIds((ids) => [...ids, d.id]);
                  }} />
                ) : null}
              />
              <label className="flex items-center gap-2 text-[11px] text-slate-600" data-testid="msg-followup">
                Wiedervorlage – Antwort erwartet bis
                <span className="w-36"><DatePicker value={followUpDate} onChange={setFollowUpDate} className="h-7 text-[11px]" data-testid="msg-followup-date" /></span>
              </label>
              {/* 2026-08 (iter111) — Ticket-Anlagen als E-Mail-Anhang mitsenden */}
              {ticketAttachments.length > 0 && (
                <div className="border border-emerald-200 bg-emerald-50/60 rounded-md p-2.5" data-testid="msg-ticket-attachments">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold mb-1.5 flex items-center gap-1">
                    Ticket-Anlagen ({(Object.values(ticketAttachmentSelected).filter(Boolean).length)}/{ticketAttachments.length})
                  </div>
                  <div className="space-y-1.5">
                    {ticketAttachments.map((a) => (
                      <label key={a.id} className="flex items-center gap-2 text-xs text-slate-800 cursor-pointer" data-testid={`ticket-att-item-${a.id}`}>
                        <Checkbox
                          checked={!!ticketAttachmentSelected[a.id]}
                          onCheckedChange={(v) => setTicketAttachmentSelected((s) => ({ ...s, [a.id]: !!v }))}
                          data-testid={`ticket-att-cb-${a.id}`}
                        />
                        <span className="truncate flex-1" title={a.filename}>
                          {(a.content_type || "").startsWith("image/") ? "🖼️ " : "📎 "}
                          {a.filename}
                        </span>
                        <span className="text-[10px] text-slate-500 tabular-nums">
                          {a.size ? `${Math.round(a.size / 1024)} KB` : ""}
                        </span>
                      </label>
                    ))}
                  </div>
                  <div className="text-[10px] text-emerald-800 mt-2">
                    Diese Fotos/PDFs aus dem Ticket werden bei E-Mail-Versand mit angehängt.
                  </div>
                </div>
              )}
              <div className="text-[10px] text-slate-500 mt-1.5 italic">
                Diese Dokumente werden nur bei E-Mail-Versand mitgeschickt und beim Hauptbrief als verknüpft gespeichert.
              </div>
            </div>
          </aside>

          {/* Hauptbereich: Ribbon + Papier (volle verfügbare Breite) */}
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
            {/* Desktop-Ribbon (nur ab md sichtbar) */}
            <div className="hidden md:block">
              <WordRibbon
                editor={editor}
                snippets={templates}
                onInsertSnippet={applyTemplate}
                onOpenSnippetPicker={() => setSnippetOpen(true)}
                categories={[]}
              />
            </div>
            {/* VM-7b Mobile-Toolbar (nur <md) — kompakt, ein Row */}
            <div className="md:hidden flex items-center gap-1 px-2 py-1.5 border-b border-slate-200 bg-slate-50 overflow-x-auto" data-testid="msg-mobile-toolbar">
              <MobileEditorButton editor={editor} action="toggleBold" label="B" bold testid="mob-bold" />
              <MobileEditorButton editor={editor} action="toggleItalic" label="I" italic testid="mob-italic" />
              <MobileEditorButton editor={editor} action="toggleUnderline" label="U" underline testid="mob-underline" />
              <span className="w-px h-5 bg-slate-300 mx-1" />
              <MobileEditorButton editor={editor} align="left" label="⇤" testid="mob-align-left" />
              <MobileEditorButton editor={editor} align="center" label="↔" testid="mob-align-center" />
              <MobileEditorButton editor={editor} align="right" label="⇥" testid="mob-align-right" />
              <MobileEditorButton editor={editor} align="justify" label="≡" testid="mob-align-justify" />
              <span className="w-px h-5 bg-slate-300 mx-1" />
              <MobileEditorButton editor={editor} action="toggleBulletList" label="•" testid="mob-ul" />
              <MobileEditorButton editor={editor} action="toggleOrderedList" label="1." testid="mob-ol" />
              <span className="w-px h-5 bg-slate-300 mx-1" />
              <button
                onClick={() => setSnippetOpen(true)}
                className="text-xs px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold shrink-0"
                data-testid="mob-snippet-btn"
              >
                + Bausteine
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto bg-slate-100 p-2 md:p-6">
              <div
                className="bg-white shadow-sm mx-auto w-full cursor-text"
                style={{ maxWidth: "820px", minHeight: "500px" }}
                onClick={(e) => {
                  // VM-7d — Hitbox auf gesamtem DIN A4-Blatt: Klick auf Rand fokussiert Editor am Ende
                  if (editor && e.target === e.currentTarget) editor.chain().focus("end").run();
                }}
                data-testid="msg-paper"
              >
                <div
                  className="px-4 py-5 md:px-14 md:py-12"
                  style={{
                    fontFamily: userFont.family || undefined,
                    fontSize: userFont.size ? `${userFont.size}pt` : undefined,
                  }}
                  onClick={(e) => {
                    if (editor && e.target === e.currentTarget) editor.chain().focus("end").run();
                  }}
                >
                  <EditorContent editor={editor} data-testid="msg-editor" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="px-3 md:px-5 py-2 md:py-2.5 border-t border-slate-200 bg-white gap-2 shrink-0 flex-row flex-wrap md:flex-nowrap">
          <Button variant="outline" type="button" onClick={handleDownloadPreviewPdf} className="gap-2 flex-1 md:flex-none" data-testid="msg-pdf-preview">
            <FilePdf size={14} /> <span className="hidden sm:inline">PDF-Vorschau</span><span className="sm:hidden">PDF</span>
          </Button>
          <Button
            onClick={() => setSendModalOpen(true)}
            disabled={sending}
            className="gap-2 bg-slate-900 hover:bg-slate-800 flex-1 md:flex-none"
            data-testid="msg-send-btn"
          >
            <PaperPlaneTilt size={14} /> <span className="hidden sm:inline">Versenden & speichern</span><span className="sm:hidden">Senden</span>
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Sprint 5j — Send-Modal: Versandweg + ggf. Adress-Picker */}
      <SendMethodModal
        open={sendModalOpen}
        onOpenChange={setSendModalOpen}
        selectedRecipients={selectedRecipients}
        recipientCandidates={recipientCandidates}
        channels={channels}
        setChannels={setChannels}
        emailOverride={emailOverride}
        setEmailOverride={setEmailOverride}
        emailToList={emailToList}
        setEmailToList={setEmailToList}
        emailBcc={emailBcc}
        setEmailBcc={setEmailBcc}
        emailCc={emailCc}
        setEmailCc={setEmailCc}
        availableEmails={availableEmails}
        printAfterPost={printAfterPost}
        setPrintAfterPost={setPrintAfterPost}
        wet={{
          enabled: wetSignature, setEnabled: setWetSignature,
          scan: signedScan, setScan: setSignedScan,
          pending: scanPending, setPending: setScanPending,
          onPrint: printForSignature,
          signers: wetSigners, setSigners: setWetSigners, suggestions: signerSuggestions,
          label: subject,
        }}
        wetDisabled={!!(rundschreiben && wizardRecipients && wizardRecipients.length > 1)}
        onConfirm={async () => { setSendModalOpen(false); await handleSend(); }}
        sending={sending}
      />

      <SnippetPicker
        open={snippetOpen}
        onOpenChange={setSnippetOpen}
        onInsert={(html, tpl) => {
          if (editor) editor.chain().focus().insertContent(html).run();
          // Vorlage (nicht Baustein): Betreff/Unterbetreff mit übernehmen
          if (tpl) {
            if (tpl.subject && !subject.trim()) setSubject(tpl.subject);
            if ((tpl.sub_subject || "").trim()) { subSubjectFromTpl.current = true; setSubSubject(tpl.sub_subject); }
          }
        }}
      />

      {/* 2026-02 — Inline-Bestätigung bei fehlendem Betreff (statt Browser-Popup) */}
      <Dialog open={missingSubjectPrompt} onOpenChange={setMissingSubjectPrompt}>
        <DialogContent className="max-w-md" data-testid="missing-subject-dialog">
          <DialogHeader>
            <DialogTitle>Kein Betreff angegeben</DialogTitle>
            <DialogDescription>
              Sie haben keinen Betreff eingetragen. Soll das Schreiben mit dem Standard-Titel
              <strong> &bdquo;Schreiben&ldquo;</strong> versendet werden, oder möchten Sie den Betreff jetzt eingeben?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setMissingSubjectPrompt(false);
                // Fokus zurück auf Betreff-Feld
                setTimeout(() => {
                  const el = document.querySelector('[data-testid="msg-subject"]');
                  if (el) el.focus();
                }, 100);
              }}
              data-testid="missing-subject-cancel"
            >
              Betreff eingeben
            </Button>
            <Button
              className="bg-slate-900 hover:bg-slate-800"
              onClick={async () => {
                setMissingSubjectPrompt(false);
                await handleSend(true);
              }}
              data-testid="missing-subject-continue"
            >
              &bdquo;Schreiben&ldquo; verwenden &amp; senden
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* VM-7c — In-App PDF-Vorschau (umgeht Popup-Blocker & about:blank) */}
      <Dialog open={pdfPreviewOpen} onOpenChange={(v) => (v ? setPdfPreviewOpen(true) : closePdfPreview())}>
        <DialogContent
          className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] p-0 gap-0 flex flex-col overflow-hidden"
          data-testid="pdf-preview-dialog"
        >
          <DialogHeader className="px-4 py-2 border-b border-slate-200 bg-white shrink-0 flex flex-row items-center justify-between gap-2 space-y-0">
            <DialogTitle className="text-base flex items-center gap-2">
              <FilePdf size={16} /> PDF-Vorschau
            </DialogTitle>
            <DialogDescription className="sr-only">Vorschau des generierten Briefes als PDF</DialogDescription>
            <div className="flex items-center gap-2">
              {pdfPreviewUrl && !pdfPreviewLoading && !pdfPreviewError && (
                <>
                  <a
                    href={pdfPreviewUrl}
                    download={`vorschau-${(subject || "brief").replace(/[^a-zA-Z0-9._-]+/g, "-").slice(0, 50)}.pdf`}
                    className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 font-medium text-slate-700 inline-flex items-center gap-1.5"
                    data-testid="pdf-preview-download"
                  >
                    <Download size={12} /> Herunterladen
                  </a>
                  <button
                    onClick={() => window.open(pdfPreviewUrl, "_blank", "noopener,noreferrer")}
                    className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 font-medium text-slate-700 inline-flex items-center gap-1.5 hidden sm:inline-flex"
                    data-testid="pdf-preview-open-tab"
                  >
                    In neuem Tab öffnen
                  </button>
                </>
              )}
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-200 relative" data-testid="pdf-preview-body">
            {pdfPreviewLoading && (
              <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm" data-testid="pdf-preview-loading">
                <div className="text-center">
                  <div className="inline-block w-6 h-6 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mb-2" />
                  <div>Vorschau wird erzeugt …</div>
                </div>
              </div>
            )}
            {!pdfPreviewLoading && pdfPreviewError && (
              <div className="absolute inset-0 flex items-center justify-center p-4" data-testid="pdf-preview-error">
                <div className="text-center max-w-md">
                  <div className="text-rose-700 font-semibold mb-2">Fehler beim Erzeugen der Vorschau</div>
                  <div className="text-sm text-slate-600 break-words">{pdfPreviewError}</div>
                  <Button onClick={handleDownloadPreviewPdf} variant="outline" className="mt-4" data-testid="pdf-preview-retry">
                    Erneut versuchen
                  </Button>
                </div>
              </div>
            )}
            {!pdfPreviewLoading && !pdfPreviewError && pdfPreviewUrl && (
              <iframe
                src={pdfPreviewUrl}
                title="PDF-Vorschau"
                className="absolute inset-0 w-full h-full border-0 bg-white"
                data-testid="pdf-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 2026-08 — Exit-Guard: Bestätigung nur wenn substantieller Inhalt existiert */}
      <Dialog open={open && confirmExit && !justSentRef.current} onOpenChange={(v) => !v && setConfirmExit(false)}>
        <DialogContent className="max-w-xl" data-testid="msg-exit-guard">
          <DialogHeader>
            <DialogTitle>Editor wirklich verlassen?</DialogTitle>
            <DialogDescription>
              Sie haben bereits Inhalt geschrieben. Wenn Sie jetzt schließen, gehen alle Änderungen verloren, die seit dem letzten Auto-Speichern nicht gesichert wurden.
              {draftSavedIso && (
                <span className="block mt-2 text-xs text-emerald-700">
                  Letzter Auto-Speicherstand: {new Date(draftSavedIso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} — dieser Entwurf bleibt in der Entwurfsliste erhalten.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-end flex-wrap">
            <Button variant="ghost" onClick={() => setConfirmExit(false)}
                    className="w-full sm:w-auto"
                    data-testid="msg-exit-cancel">
              Weiter bearbeiten
            </Button>
            <Button variant="outline" onClick={confirmDiscardAndClose}
                    className="w-full sm:w-auto"
                    data-testid="msg-exit-discard">
              Schließen
            </Button>
            <Button
              variant="default"
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={async () => {
                await saveDraftNow({ silent: false });
                setConfirmExit(false);
                onOpenChange(false);
              }}
              data-testid="msg-exit-save-draft"
            >
              Als Entwurf speichern &amp; schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
